import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  formatPrice,
  GRACE_DAYS,
  priceFor,
  TRIAL_FRAME_LIMIT,
  type BillingInterval,
  type BillingSummary,
} from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import { daysUntil, longDate } from '../lib/standing';
import { Shell } from '../components/Shell';

interface Props {
  readonly session: StoredSession;
  readonly onSignOut: () => void;
  /** Set on the way back from Stripe's checkout. */
  readonly checkout: 'success' | null;
}

/** How long to keep asking after checkout before giving up on the webhook. */
const SETTLE_ATTEMPTS = 5;
const SETTLE_INTERVAL_MS = 2000;

const INTERVAL_WORD: Record<BillingInterval, string> = { month: 'month', year: 'year' };

/**
 * The money page. Everything on it is the server's word — `standing`, the
 * limit, the dates — and the page never works any of it out for itself. What it
 * adds is the plan picker: an interval and a count, priced in front of the
 * person before they leave for Stripe.
 */
export const Billing = ({ session, onSignOut, checkout }: Props) => {
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [interval, setInterval_] = useState<BillingInterval>('month');
  const [quantity, setQuantity] = useState(1);
  /** The count being edited on an existing subscription. */
  const [limit, setLimit] = useState<number | null>(null);

  const load = useCallback(async () => {
    const result = await api.billing(session.token);
    setSummary(result);
    setQuantity((current) => Math.max(current, result.framesOwned, 1));
    setLimit(result.subscription?.frameLimit ?? null);
    return result;
  }, [session.token]);

  useEffect(() => {
    let live = true;
    const settle = async () => {
      try {
        if (checkout !== 'success') {
          await load();
          return;
        }
        // Back from Stripe. The webhook usually beats the browser here, but
        // not always, so ask Stripe directly first and then give the webhook a
        // few seconds before declaring the page settled either way.
        let result = await api.syncBilling(session.token);
        for (let attempt = 0; attempt < SETTLE_ATTEMPTS && live; attempt += 1) {
          if (result.subscription && result.standing === 'live') break;
          await new Promise((resolve) => setTimeout(resolve, SETTLE_INTERVAL_MS));
          result = await api.syncBilling(session.token);
        }
        if (!live) return;
        setSummary(result);
        setLimit(result.subscription?.frameLimit ?? null);
        setNote(
          result.subscription && result.standing === 'live'
            ? 'Thank you. Your subscription is active and your frames are live.'
            : 'Stripe is still confirming the payment. This page will show it once it has.',
        );
      } catch (caught) {
        if (live) setError(caught instanceof Error ? caught.message : 'Could not load billing.');
      }
    };
    void settle();
    return () => {
      live = false;
    };
  }, [checkout, load, session.token]);

  const run = async (task: () => Promise<string | null>) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const message = await task();
      if (message) setNote(message);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const leaveFor = (url: string) => {
    window.location.assign(url);
  };

  const shell = (children: ReactNode) => (
    <Shell who={session.account.name} onSignOut={onSignOut} back={{ label: 'Account', to: '/account' }}>
      <div className="stack-loose" style={{ marginTop: '2rem' }}>
        <div>
          <p className="eyebrow">Billing</p>
          <h1 className="title">Frames, and what they cost.</h1>
        </div>
        {error && <p className="alert">{error}</p>}
        {note && (
          <p className="alert" data-tone="ok">
            {note}
          </p>
        )}
        {children}
      </div>
    </Shell>
  );

  if (!summary) return shell(<p className="empty">Loading</p>);

  if (!summary.enabled) {
    return shell(
      <p className="note">
        This copy of Mantelpiece is not set up to take payments, so there is nothing to do here.
        Every frame is live for as long as it is running.
      </p>,
    );
  }

  const { subscription, standing } = summary;
  const paidUp = subscription !== null && standing === 'live' && summary.frameLimit > 0 &&
    (subscription.status === 'active' || subscription.status === 'trialing');
  const onTrial = subscription === null && standing === 'live';
  const minQuantity = Math.max(1, summary.framesOwned);
  const pricing = summary.pricing[interval];

  return shell(
    <>
      {/* ------------------------------------------------------- standing -- */}
      <div className="stack">
        <div className="section-head">
          <p className="eyebrow">Where you stand</p>
        </div>

        {onTrial && summary.standingUntil !== null && (
          <p className="lede">
            You are on the free trial. It covers {TRIAL_FRAME_LIMIT === 1 ? 'one frame' : `${TRIAL_FRAME_LIMIT} frames`}{' '}
            until {longDate(summary.standingUntil)} — {daysUntil(summary.standingUntil)} day
            {daysUntil(summary.standingUntil) === 1 ? '' : 's'} from now. Subscribe whenever you
            like; nothing is charged until you do.
          </p>
        )}

        {paidUp && subscription && (
          <>
            <dl className="facts">
              <div>
                <dt>Plan</dt>
                <dd>
                  {subscription.frameLimit} frame{subscription.frameLimit === 1 ? '' : 's'},{' '}
                  {formatPrice(priceFor(subscription.interval, subscription.frameLimit))} a{' '}
                  {INTERVAL_WORD[subscription.interval]}
                </dd>
              </div>
              <div>
                <dt>{subscription.cancelAtPeriodEnd ? 'Ends' : 'Renews'}</dt>
                <dd>{longDate(subscription.currentPeriodEnd)}</dd>
              </div>
              <div>
                <dt>Frames</dt>
                <dd>
                  {summary.framesOwned} of {subscription.frameLimit} in use
                </dd>
              </div>
            </dl>
            {subscription.cancelAtPeriodEnd && (
              <p className="alert">
                This subscription is set to end. Your frames go read-only from{' '}
                {longDate(subscription.currentPeriodEnd)}, keep showing for {GRACE_DAYS} days, and
                then come off their screens. Nothing is deleted. Manage billing to keep it going.
              </p>
            )}
          </>
        )}

        {standing === 'grace' && (
          <p className="alert">
            {subscription ? 'Your subscription has lapsed.' : 'Your trial has ended.'} Your frames
            keep showing what is on them
            {summary.standingUntil ? ` until ${longDate(summary.standingUntil)}` : ''}, but nothing
            new can be sent to them. Subscribe below and they are live again straight away.
          </p>
        )}

        {standing === 'dormant' && (
          <p className="alert">
            Your frames are off their screens: the grace period after the{' '}
            {subscription ? 'subscription lapsed' : 'trial ended'} has run out. Nothing has been
            deleted. Subscribe below and every photo comes straight back.
          </p>
        )}
      </div>

      {/* --------------------------------------------------- subscribed -- */}
      {paidUp && subscription && (
        <div className="stack">
          <div className="section-head">
            <p className="eyebrow">Change it</p>
          </div>
          <p className="note">
            Add a frame and the difference is charged pro rata for the rest of this{' '}
            {INTERVAL_WORD[subscription.interval]}. To take one away, delete the frame first — which
            frames survive is your call, not ours.
          </p>
          <div className="row">
            <div className="field">
              <span>Frames</span>
              <div className="stepper" role="group" aria-label="Number of frames">
                <button
                  type="button"
                  className="button button-quiet button-small"
                  aria-label="One fewer frame"
                  disabled={busy || (limit ?? 1) <= minQuantity}
                  onClick={() => setLimit((current) => Math.max(minQuantity, (current ?? 1) - 1))}
                >
                  −
                </button>
                <output aria-live="polite">{limit ?? subscription.frameLimit}</output>
                <button
                  type="button"
                  className="button button-quiet button-small"
                  aria-label="One more frame"
                  disabled={busy}
                  onClick={() => setLimit((current) => (current ?? 1) + 1)}
                >
                  +
                </button>
              </div>
            </div>
            <p className="price-line" aria-live="polite">
              {formatPrice(priceFor(subscription.interval, limit ?? subscription.frameLimit))}
              <span> a {INTERVAL_WORD[subscription.interval]}</span>
            </p>
            <button
              type="button"
              className="button"
              disabled={busy || limit === null || limit === subscription.frameLimit}
              onClick={() =>
                void run(async () => {
                  if (limit === null) return null;
                  await api.setFrameLimit(session.token, limit);
                  await load();
                  return `Your subscription now covers ${limit} frame${limit === 1 ? '' : 's'}.`;
                })
              }
            >
              Update
            </button>
          </div>
          <div className="row">
            <button
              type="button"
              className="button button-quiet"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  leaveFor((await api.openBillingPortal(session.token)).url);
                  return null;
                })
              }
            >
              Manage billing
            </button>
            <p className="note">Card, invoices, switching monthly and yearly, or cancelling.</p>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------ subscribe -- */}
      {!paidUp && (
        <div className="stack">
          <div className="section-head">
            <p className="eyebrow">Subscribe</p>
          </div>

          <p className="note">
            You pay for frames, and only frames. Screens are free, however many. People you invite
            never pay, and neither does anyone sending through an open link.
          </p>

          <div className="chips" role="group" aria-label="Billing interval">
            {(['month', 'year'] as const).map((option) => (
              <button
                key={option}
                type="button"
                className="chip"
                aria-pressed={interval === option}
                onClick={() => setInterval_(option)}
              >
                <span className="chip-name">{option === 'month' ? 'Monthly' : 'Yearly'}</span>
                <span className="chip-note">
                  {option === 'month'
                    ? `${formatPrice(summary.pricing.month.firstFrame)} a month`
                    : `${formatPrice(summary.pricing.year.firstFrame)} a year — about two months free`}
                </span>
              </button>
            ))}
          </div>

          <div className="row">
            <div className="field">
              <span>Frames</span>
              <div className="stepper" role="group" aria-label="Number of frames">
                <button
                  type="button"
                  className="button button-quiet button-small"
                  aria-label="One fewer frame"
                  disabled={busy || quantity <= minQuantity}
                  onClick={() => setQuantity((current) => Math.max(minQuantity, current - 1))}
                >
                  −
                </button>
                <output aria-live="polite">{quantity}</output>
                <button
                  type="button"
                  className="button button-quiet button-small"
                  aria-label="One more frame"
                  disabled={busy}
                  onClick={() => setQuantity((current) => current + 1)}
                >
                  +
                </button>
              </div>
            </div>
            <p className="price-line" aria-live="polite">
              {formatPrice(priceFor(interval, quantity))}
              <span> a {INTERVAL_WORD[interval]}</span>
            </p>
          </div>

          <p className="note">
            The first frame is {formatPrice(pricing.firstFrame)} a {INTERVAL_WORD[interval]}; each
            one after it is {formatPrice(pricing.additionalFrame)}.
            {summary.framesOwned > 1
              ? ` You own ${summary.framesOwned} frames, so that is the fewest you can choose.`
              : ''}
          </p>

          <div className="row">
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  leaveFor((await api.startCheckout(session.token, interval, quantity)).url);
                  return null;
                })
              }
            >
              {busy ? 'Working' : 'Continue to checkout'}
            </button>
            {subscription && (
              <button
                type="button"
                className="button button-quiet"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    leaveFor((await api.openBillingPortal(session.token)).url);
                    return null;
                  })
                }
              >
                Past invoices
              </button>
            )}
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- small print -- */}
      <div className="stack">
        <div className="section-head">
          <p className="eyebrow">The small print</p>
        </div>
        <ul className="points">
          <li>
            <strong>Stripe is the merchant.</strong> Checkout, receipts and any VAT are Stripe's, and
            it shows the price in your own currency before you pay. Prices above are in pounds.
          </li>
          <li>
            <strong>Nothing is ever deleted for not paying.</strong> When a subscription lapses your
            frames go read-only, keep showing for {GRACE_DAYS} days, then come off their screens
            until it is renewed. The photos wait.
          </li>
          <li>
            <strong>Cancel whenever.</strong> The subscription runs to the end of what you have
            paid for and stops. The billing portal above is where.
          </li>
        </ul>
      </div>
    </>,
  );
};
